/**
 * AdminComms — Platform-wide delivery report (WhatsApp / Email / Push).
 *
 * Super-admin can slice-and-dice `comm_audit_log` by channel, direction,
 * status, event, tenant, date range and free-text (recipient / subject /
 * body). Row click opens a drawer with the full body, provider response,
 * delivery timeline and WhatsApp read receipts.
 *
 * Backed by /api/admin/comms/report + /report/facets + /message/{id}.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Mail, MessageSquare, Bell, Filter, X, RefreshCw, Loader2, CheckCircle2, AlertCircle, Clock, Eye } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, EmptyState, Input } from "@/components/Ui";
import { fmtDateTime } from "@/lib/time";

const CHANNEL_META = {
    email:    { icon: Mail,          label: "Email",     tone: "text-sky-400" },
    whatsapp: { icon: MessageSquare, label: "WhatsApp",  tone: "text-emerald-400" },
    push:     { icon: Bell,          label: "Push",      tone: "text-amber-400" },
    sms:      { icon: MessageSquare, label: "SMS",       tone: "text-indigo-400" },
};

const STATUS_TONE = {
    sent:      "text-sky-400 border-sky-400/40",
    delivered: "text-emerald-400 border-emerald-400/40",
    read:      "text-emerald-500 border-emerald-500/60",
    failed:    "text-red-400 border-red-400/50",
    queued:    "text-muted-foreground border-border",
    bounced:   "text-orange-400 border-orange-400/50",
};

function Pill({ children, className = "", testId }) {
    return (
        <span data-testid={testId}
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-full ${className}`}>
            {children}
        </span>
    );
}

function ChannelBadge({ channel }) {
    const meta = CHANNEL_META[channel] || { icon: MessageSquare, label: channel || "—", tone: "text-muted-foreground" };
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs ${meta.tone}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
            {meta.label}
        </span>
    );
}

function StatusPill({ status }) {
    const s = (status || "queued").toLowerCase();
    return <Pill testId={`comm-status-${s}`} className={STATUS_TONE[s] || "text-muted-foreground border-border"}>{s}</Pill>;
}

/* ------------------------------------------------------------------ */
/* Drawer with full detail + timeline + receipts                       */
/* ------------------------------------------------------------------ */
function DetailDrawer({ msgId, onClose }) {
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        if (!msgId) return;
        let alive = true;
        setLoading(true);
        api.get(`/admin/comms/message/${msgId}`)
            .then(r => { if (alive) setDetail(r.data); })
            .catch(e => toast.error(apiError(e)))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [msgId]);

    const timeline = useMemo(() => {
        if (!detail) return [];
        const steps = [
            { key: "created",   label: "Created",   at: detail.created_at,   icon: Clock },
            { key: "sent",      label: "Sent",      at: detail.sent_at,      icon: CheckCircle2 },
            { key: "delivered", label: "Delivered", at: detail.delivered_at, icon: CheckCircle2 },
            { key: "read",      label: "Read",      at: detail.read_at,      icon: Eye },
        ];
        return steps.filter(s => s.at || s.key === "created");
    }, [detail]);

    if (!msgId) return null;

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="comm-detail-drawer">
            <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative ml-auto w-full max-w-2xl h-full bg-muted border-l border-border overflow-y-auto">
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-border bg-muted/95 backdrop-blur">
                    <div>
                        <div className="overline mb-1">Delivery detail</div>
                        <div className="font-serif text-2xl">Message audit</div>
                    </div>
                    <button onClick={onClose} data-testid="comm-detail-close"
                        className="text-muted-foreground hover:text-foreground p-2">
                        <X className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </div>

                {loading ? (
                    <div className="p-12 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
                    </div>
                ) : !detail ? (
                    <div className="p-6 text-sm text-muted-foreground">Not found.</div>
                ) : (
                    <div className="p-6 space-y-8">
                        {/* Header meta */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Channel</div>
                                <ChannelBadge channel={detail.channel} />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Status</div>
                                <StatusPill status={detail.status} />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">To</div>
                                <div className="font-mono text-xs break-all">{detail.to || "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Tenant</div>
                                <div className="text-xs">
                                    {detail.tenant_name || <span className="text-muted-foreground">Platform</span>}
                                    {detail.tenant_id && <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{detail.tenant_id}</div>}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Purpose (event)</div>
                                <div className="font-mono text-xs">{detail.event || "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Provider</div>
                                <div className="font-mono text-xs">{detail.provider || "—"}</div>
                            </div>
                            {detail.subject && (
                                <div className="col-span-2">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Subject</div>
                                    <div className="text-sm">{detail.subject}</div>
                                </div>
                            )}
                        </div>

                        {/* Timeline */}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Delivery timeline</div>
                            <div className="space-y-2">
                                {timeline.map(step => {
                                    const Icon = step.icon;
                                    const done = !!step.at;
                                    return (
                                        <div key={step.key} data-testid={`timeline-${step.key}`}
                                            className="flex items-center gap-3 text-xs">
                                            <Icon className={`w-4 h-4 ${done ? "text-emerald-400" : "text-muted-foreground/50"}`} strokeWidth={1.6} />
                                            <span className={`w-24 ${done ? "" : "text-muted-foreground"}`}>{step.label}</span>
                                            <span className="font-mono text-muted-foreground">{done ? fmtDateTime(step.at) : "—"}</span>
                                        </div>
                                    );
                                })}
                                {detail.delivery_ms > 0 && (
                                    <div className="text-[10px] text-muted-foreground mt-2">
                                        Round-trip: {detail.delivery_ms} ms · Retries: {detail.retry_count || 0}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* WhatsApp receipts */}
                        {detail.receipts && detail.receipts.length > 0 && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">WhatsApp receipts</div>
                                <div className="border border-border rounded-lg divide-y divide-border">
                                    {detail.receipts.map((r, i) => (
                                        <div key={i} data-testid={`receipt-${i}`} className="flex justify-between items-center px-3 py-2 text-xs">
                                            <StatusPill status={r.status} />
                                            <span className="font-mono text-muted-foreground">{fmtDateTime(r.received_at)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Body */}
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Body</div>
                            <pre data-testid="comm-body-full"
                                className="whitespace-pre-wrap text-xs font-mono bg-background border border-border rounded-lg p-4 max-h-96 overflow-y-auto">
                                {detail.body_full || "—"}
                            </pre>
                        </div>

                        {/* Error */}
                        {detail.error && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-red-400 mb-2 flex items-center gap-1.5">
                                    <AlertCircle className="w-3 h-3" /> Error
                                </div>
                                <div className="text-xs text-red-300 bg-red-950/30 border border-red-500/30 rounded-lg p-3 font-mono">
                                    {typeof detail.error === "string" ? detail.error : JSON.stringify(detail.error, null, 2)}
                                </div>
                            </div>
                        )}

                        {/* Provider response */}
                        {detail.provider_response && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Provider response</div>
                                <pre className="whitespace-pre-wrap text-[10px] font-mono bg-background border border-border rounded-lg p-3 max-h-48 overflow-y-auto text-muted-foreground">
                                    {JSON.stringify(detail.provider_response, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Filter chip (facet count aware)                                     */
/* ------------------------------------------------------------------ */
function FilterChipBtn({ active, onClick, children, count, testId }) {
    return (
        <button onClick={onClick} data-testid={testId}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-full transition
                ${active
                    ? "border-primary text-primary bg-primary/10"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
            {children}
            {typeof count === "number" && <span className="opacity-60 font-mono">{count}</span>}
        </button>
    );
}

/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */
export default function AdminComms() {
    const [channel, setChannel] = useState("all");
    const [direction, setDirection] = useState("all");
    const [status, setStatus] = useState("all");
    const [event, setEvent] = useState("all");
    const [tenantId, setTenantId] = useState("");
    const [since, setSince] = useState("");
    const [until, setUntil] = useState("");
    const [q, setQ] = useState("");
    const [qDebounced, setQDebounced] = useState("");
    const [offset, setOffset] = useState(0);
    const [limit] = useState(50);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [facets, setFacets] = useState({ channels: [], events: [], statuses: [] });
    const [selectedId, setSelectedId] = useState(null);

    // Debounce free-text search
    useEffect(() => {
        const t = setTimeout(() => setQDebounced(q), 400);
        return () => clearTimeout(t);
    }, [q]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit, offset };
            if (channel !== "all") params.channel = channel;
            if (direction !== "all") params.direction = direction;
            if (status !== "all") params.status = status;
            if (event !== "all") params.event = event;
            if (tenantId.trim()) params.tenant_id = tenantId.trim();
            if (since) params.since = new Date(since).toISOString();
            if (until) params.until = new Date(until).toISOString();
            if (qDebounced.trim()) params.q = qDebounced.trim();
            const { data } = await api.get("/admin/comms/report", { params });
            setRows(data.rows || []);
            setTotal(data.total || 0);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [channel, direction, status, event, tenantId, since, until, qDebounced, limit, offset]);

    useEffect(() => { load(); }, [load]);

    // Reset pagination when a filter changes
    useEffect(() => { setOffset(0); }, [channel, direction, status, event, tenantId, since, until, qDebounced]);

    // Load facets once
    useEffect(() => {
        api.get("/admin/comms/report/facets")
            .then(r => setFacets(r.data || {}))
            .catch(() => {});
    }, []);

    const channelCount = useCallback((val) => {
        const row = (facets.channels || []).find(x => x.value === val);
        return row?.count;
    }, [facets]);

    const clearFilters = () => {
        setChannel("all"); setDirection("all"); setStatus("all"); setEvent("all");
        setTenantId(""); setSince(""); setUntil(""); setQ("");
    };

    const hasActiveFilter = channel !== "all" || direction !== "all" || status !== "all"
        || event !== "all" || tenantId || since || until || q;

    const page = Math.floor(offset / limit) + 1;
    const pageCount = Math.max(1, Math.ceil(total / limit));

    return (
        <>
            <PageHeader
                title="Delivery Report"
                subtitle="Every WhatsApp, Email and Push dispatched from the platform. Search by tenant, recipient, subject or body. Tap any row for the full audit trail."
                action={
                    <button data-testid="comm-refresh" onClick={load}
                        className="inline-flex items-center gap-2 text-xs border border-border px-3 py-2 hover:border-primary transition">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.6} />
                        Refresh
                    </button>
                } />

            {/* Search + tenant */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
                <div className="md:col-span-2 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.6} />
                    <Input data-testid="comm-search"
                        value={q} onChange={e => setQ(e.target.value)}
                        className="pl-10"
                        placeholder="Search recipient, subject or body..." />
                </div>
                <Input data-testid="comm-tenant-filter"
                    value={tenantId} onChange={e => setTenantId(e.target.value)}
                    placeholder="Filter by tenant ObjectId" />
            </div>

            {/* Channel chips */}
            <div className="flex flex-wrap gap-2 mb-3">
                <FilterChipBtn active={channel === "all"} onClick={() => setChannel("all")} testId="chip-channel-all">All channels</FilterChipBtn>
                <FilterChipBtn active={channel === "email"} onClick={() => setChannel("email")} count={channelCount("email")} testId="chip-channel-email">
                    <Mail className="w-3 h-3" /> Email
                </FilterChipBtn>
                <FilterChipBtn active={channel === "whatsapp"} onClick={() => setChannel("whatsapp")} count={channelCount("whatsapp")} testId="chip-channel-whatsapp">
                    <MessageSquare className="w-3 h-3" /> WhatsApp
                </FilterChipBtn>
                <FilterChipBtn active={channel === "push"} onClick={() => setChannel("push")} count={channelCount("push")} testId="chip-channel-push">
                    <Bell className="w-3 h-3" /> Push
                </FilterChipBtn>
                <FilterChipBtn active={channel === "sms"} onClick={() => setChannel("sms")} count={channelCount("sms")} testId="chip-channel-sms">
                    SMS
                </FilterChipBtn>
            </div>

            {/* Status + direction + event + dates */}
            <div className="grid md:grid-cols-5 gap-3 mb-4">
                <select data-testid="comm-status-select"
                    value={status} onChange={e => setStatus(e.target.value)}
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs">
                    <option value="all">Any status</option>
                    {(facets.statuses || []).map(s => (
                        <option key={s.value} value={s.value}>{s.value} ({s.count})</option>
                    ))}
                </select>
                <select data-testid="comm-direction-select"
                    value={direction} onChange={e => setDirection(e.target.value)}
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs">
                    <option value="all">Any direction</option>
                    <option value="out">Outbound</option>
                    <option value="in">Inbound</option>
                </select>
                <select data-testid="comm-event-select"
                    value={event} onChange={e => setEvent(e.target.value)}
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs">
                    <option value="all">Any purpose</option>
                    {(facets.events || []).map(e => (
                        <option key={e.value} value={e.value}>{e.value} ({e.count})</option>
                    ))}
                </select>
                <input data-testid="comm-since"
                    type="datetime-local" value={since} onChange={e => setSince(e.target.value)}
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs" />
                <input data-testid="comm-until"
                    type="datetime-local" value={until} onChange={e => setUntil(e.target.value)}
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs" />
            </div>

            {hasActiveFilter && (
                <div className="mb-3">
                    <button data-testid="comm-clear-filters" onClick={clearFilters}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                        <X className="w-3 h-3" /> Clear all filters
                    </button>
                </div>
            )}

            {/* Result count + pagination */}
            <div className="flex items-center justify-between mb-3 text-xs text-muted-foreground">
                <div data-testid="comm-total">
                    <Filter className="w-3 h-3 inline mr-1" />
                    <span className="font-mono">{total.toLocaleString()}</span> messages
                    {loading && <Loader2 className="w-3 h-3 inline ml-2 animate-spin" />}
                </div>
                <div className="flex items-center gap-3">
                    <button data-testid="comm-page-prev"
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                        className="px-2 py-1 border border-border disabled:opacity-30 hover:border-primary">Prev</button>
                    <span className="font-mono">{page} / {pageCount}</span>
                    <button data-testid="comm-page-next"
                        disabled={offset + limit >= total}
                        onClick={() => setOffset(offset + limit)}
                        className="px-2 py-1 border border-border disabled:opacity-30 hover:border-primary">Next</button>
                </div>
            </div>

            {/* Table */}
            {rows.length === 0 && !loading ? (
                <EmptyState title="No messages match" subtitle="Try widening the filters or clearing search." />
            ) : (
                <div className="border border-border overflow-x-auto" data-testid="comm-table">
                    <table className="min-w-[1000px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="px-4 py-3">When (IST)</th>
                                <th className="px-4 py-3">Channel</th>
                                <th className="px-4 py-3">To</th>
                                <th className="px-4 py-3">Subject / Preview</th>
                                <th className="px-4 py-3">Tenant</th>
                                <th className="px-4 py-3">Purpose</th>
                                <th className="px-4 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} data-testid={`comm-row-${r.id}`}
                                    onClick={() => setSelectedId(r.id)}
                                    className="border-b border-border hover:bg-background/40 cursor-pointer transition">
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                                        {fmtDateTime(r.sent_at || r.created_at)}
                                    </td>
                                    <td className="px-4 py-3"><ChannelBadge channel={r.channel} /></td>
                                    <td className="px-4 py-3 font-mono text-xs max-w-[220px] truncate" title={r.to}>{r.to || "—"}</td>
                                    <td className="px-4 py-3 max-w-[380px]">
                                        {r.subject && <div className="text-xs truncate">{r.subject}</div>}
                                        <div className="text-[11px] text-muted-foreground truncate">{r.body_preview || "—"}</div>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {r.tenant_name || <span className="text-muted-foreground">Platform</span>}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{r.event || "—"}</td>
                                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DetailDrawer msgId={selectedId} onClose={() => setSelectedId(null)} />
        </>
    );
}
